import * as THREE from 'three';
import type { Vec } from '../destruction/structure';
interface Particle {
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  life: number;
  max: number;
  size: number;
  color: number;
}
export class Effects {
  particles: Particle[] = [];
  mesh: THREE.InstancedMesh;
  limit = 160;
  reduced = false;
  private dummy = new THREE.Object3D();
  private color = new THREE.Color();
  private free: Particle[] = [];
  readonly scorch: THREE.InstancedMesh;
  private scorchCursor = 0;
  constructor(scene: THREE.Scene) {
    this.mesh = new THREE.InstancedMesh(
      new THREE.IcosahedronGeometry(1, 0),
      new THREE.MeshStandardMaterial({ roughness: 1, transparent: true, opacity: 0.72, depthWrite: false }),
      512,
    );
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
    for (let i = 0; i < 512; i++)
      this.free.push({
        position: new THREE.Vector3(),
        velocity: new THREE.Vector3(),
        life: 0,
        max: 0,
        size: 0,
        color: 0,
      });
    this.scorch = new THREE.InstancedMesh(
      new THREE.CircleGeometry(1, 24),
      new THREE.MeshBasicMaterial({
        color: 0x3c3630,
        transparent: true,
        opacity: 0.3,
        depthWrite: false,
        polygonOffset: true,
        polygonOffsetFactor: -1,
      }),
      48,
    );
    this.scorch.count = 0;
    this.scorch.frustumCulled = false;
    scene.add(this.scorch);
  }
  burst(point: Vec, color: number, amount: number, power: number): void {
    const count = this.reduced ? Math.min(4, amount) : amount;
    for (let i = 0; i < count; i++) {
      if (this.particles.length >= Math.min(512, this.limit)) this.free.push(this.particles.shift()!);
      const life = 0.55 + Math.random() * 0.8;
      const p = this.free.pop();
      if (!p) break;
      p.position.set(point.x, point.y, point.z);
      p.velocity.set(
        (Math.random() - 0.5) * power,
        Math.random() * power * 0.65 + 1,
        (Math.random() - 0.5) * power,
      );
      p.life = life;
      p.max = life;
      p.size = 0.08 + Math.random() * 0.26;
      p.color = color;
      this.particles.push(p);
    }
  }
  mark(point: Vec, radius: number): void {
    this.dummy.position.set(point.x, 0.185, point.z);
    this.dummy.rotation.set(-Math.PI / 2, 0, 0);
    this.dummy.scale.set(radius, radius * 0.84, 1);
    this.dummy.updateMatrix();
    this.scorch.setMatrixAt(this.scorchCursor++ % 48, this.dummy.matrix);
    this.scorch.count = Math.min(48, this.scorchCursor);
    this.scorch.instanceMatrix.needsUpdate = true;
  }
  update(dt: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life -= dt;
      if (p.life <= 0) {
        this.free.push(p);
        this.particles.splice(i, 1);
        continue;
      }
      p.velocity.y -= dt * 5;
      p.position.addScaledVector(p.velocity, dt);
      p.position.y = Math.max(0.22, p.position.y);
      this.dummy.position.copy(p.position);
      this.dummy.rotation.set(p.life, p.life * 1.5, 0);
      this.dummy.scale.setScalar(p.size * Math.min(1, p.life * 3));
      this.dummy.updateMatrix();
      this.mesh.setMatrixAt(i, this.dummy.matrix);
      this.mesh.setColorAt(i, this.color.setHex(p.color));
    }
    this.mesh.count = this.particles.length;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }
  clear(): void {
    this.free.push(...this.particles);
    this.particles.length = 0;
    this.mesh.count = 0;
    this.scorch.count = 0;
    this.scorchCursor = 0;
  }
}
